import { Tooltip } from '@mantine/core';
import { IconChartBar, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { followLink } from '../../shell-context.js';
import type { FleetPmsRow } from '../dashboard-types.js';
import classes from './Dashboard.module.css';

/**
 * Uyum yüzdesi (K23; BIMCO PI065): dönemde vadesi gelen işi olmayan gemide uydurma "100%" yerine
 * "—" (veri yok).
 */
export function fleetComplianceText(row: FleetPmsRow, language: string): string {
  if (row.compliance.due === 0) return '—';
  const format = new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-US', {
    maximumFractionDigits: 1,
  });
  return language === 'tr'
    ? `%${format.format(row.compliance.percent)}`
    : `${format.format(row.compliance.percent)}%`;
}

const toneOf = (percent: number) =>
  percent >= 95 ? 'success' : percent >= 85 ? 'warning' : 'danger';

/**
 * Ofis panosunun PMS kartı (plan M7 Task 21c; PMS belgesi §22): gemi tablosu (Vessel, PMS
 * Compliance, Overdue, Critical Overdue) ve verinin tarihi. Satır geminin Equipment List'ine
 * `?vessel=` ile iner; oradan işe, iş emrine ve kanıta inilir.
 */
export function FleetPmsCard({
  rows,
  onOpen,
}: {
  rows: readonly FleetPmsRow[];
  onOpen: (path: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.dashboard.${key}`, options);
  const language = i18n.resolvedLanguage ?? 'en';
  const number = (value: number) => new Intl.NumberFormat(language).format(value);
  return (
    <Panel title={k('fleetTitle')} icon={<IconChartBar size={18} stroke={1.8} />} flush>
      {rows.length === 0 ? (
        <EmptyState className={classes.empty} title={k('empty')} />
      ) : (
        <div className={classes.tableWrap}>
          <table className={classes.table} data-fleet>
            <thead>
              <tr>
                <th scope="col">{k('vessel')}</th>
                <th scope="col">
                  <span className={classes.headHint}>
                    {k('compliance')}
                    <Tooltip label={k('complianceHint')} multiline w={280}>
                      <span
                        className={classes.hintIcon}
                        role="img"
                        aria-label={k('complianceHint')}
                      >
                        <IconInfoCircle size={14} aria-hidden />
                      </span>
                    </Tooltip>
                  </span>
                </th>
                <th scope="col" data-numeric>
                  {k('overdue')}
                </th>
                <th scope="col" data-numeric>
                  {k('criticalOverdue')}
                </th>
                <th scope="col" data-wide>
                  {k('dataAsOf')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const path = `/pms/equipment?vessel=${encodeURIComponent(row.vesselId)}`;
                const percent = fleetComplianceText(row, language);
                return (
                  <tr key={row.vesselId}>
                    <td className={classes.strong}>
                      <a
                        href={path}
                        className={classes.rowLink}
                        draggable={false}
                        aria-label={k('open', { name: row.vesselName })}
                        onClick={(event) => followLink(event, path, onOpen)}
                      >
                        {row.vesselName}
                      </a>
                    </td>
                    <td>
                      {row.compliance.due === 0 ? (
                        <span className={classes.muted}>{k('noData')}</span>
                      ) : (
                        <span className={classes.compliance}>
                          <span
                            className={classes.bar}
                            data-tone={toneOf(row.compliance.percent)}
                            aria-hidden
                          >
                            <span style={{ width: `${Math.max(2, row.compliance.percent)}%` }} />
                          </span>
                          <span className={classes.percent}>{percent}</span>
                        </span>
                      )}
                    </td>
                    <td data-numeric>{number(row.overdue)}</td>
                    <td data-numeric>
                      {row.criticalOverdue > 0 ? (
                        <StatusBadge tone="danger">{number(row.criticalOverdue)}</StatusBadge>
                      ) : (
                        number(0)
                      )}
                    </td>
                    <td data-wide className={classes.muted}>
                      {row.writer === 'cloud'
                        ? k('setup')
                        : row.dataAsOf
                          ? formatDateTime(row.dataAsOf)
                          : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
